import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ApiKeyControl from './ApiKeyControl';
import AccountControl from '../auth/AccountControl';
import { useAuth } from '../../auth/AuthProvider';
import { getWorkflowSummary } from '../../services/workflowApi';
import './AppShell.css';

// How often the pending-approvals count refreshes. Long enough to be free,
// short enough that a nav badge is never badly out of date.
const COUNT_POLL_MS = 60000;

/* Simple stroke icons (16px grid, currentColor) */
const Icon = ({ d, ...rest }) => (
  <svg
    className="app-nav-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {d}
  </svg>
);

const ICONS = {
  credit: (
    <Icon
      d={
        <>
          <rect x="2.5" y="5" width="19" height="14" rx="2" />
          <path d="M2.5 9.5h19" />
          <path d="M6 14.5h4" />
        </>
      }
    />
  ),
  customers: (
    <Icon
      d={
        <>
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
          <path d="M16 5.2a3.5 3.5 0 0 1 0 5.6" />
          <path d="M18.5 14.6c1.9 1 3 2.9 3 5.4" />
        </>
      }
    />
  ),
  products: (
    <Icon
      d={
        <>
          <path d="M12 2.5 21 7v10l-9 4.5L3 17V7l9-4.5z" />
          <path d="M3 7l9 4.5L21 7" />
          <path d="M12 11.5V21.5" />
        </>
      }
    />
  ),
  pricing: (
    <Icon
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M15 9.5c-.6-1-1.7-1.5-3-1.5-1.8 0-3 1-3 2.2 0 3 6 1.6 6 4.6 0 1.2-1.2 2.2-3 2.2-1.3 0-2.4-.5-3-1.5" />
          <path d="M12 6.5v11" />
        </>
      }
    />
  ),
  inventory: (
    <Icon
      d={
        <>
          <path d="M3 9.5 12 4l9 5.5" />
          <path d="M5 11v8h14v-8" />
          <path d="M9.5 19v-5h5v5" />
        </>
      }
    />
  ),
  jobs: (
    <Icon
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3.5 2" />
        </>
      }
    />
  ),
  manualSyncs: (
    <Icon
      d={
        <>
          <path d="M20 11a8 8 0 0 0-14.3-4.5M4 4v3.5h3.5" />
          <path d="M4 13a8 8 0 0 0 14.3 4.5M20 20v-3.5h-3.5" />
        </>
      }
    />
  ),
  pim: (
    <Icon
      d={
        <>
          <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
          <rect x="13.5" y="3.5" width="7" height="7" rx="1.2" />
          <rect x="3.5" y="13.5" width="7" height="7" rx="1.2" />
          <path d="M13.5 17h7M17 13.5v7" />
        </>
      }
    />
  ),
  configuration: (
    <Icon
      d={
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.12-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.12 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.08A1.7 1.7 0 0 0 10.1 3.9V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0 1.87.34v.08a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.56 1.03z" />
        </>
      }
    />
  ),
  orders: (
    <Icon
      d={
        <>
          <path d="M6 3.5h9l3 3V20.5H6z" />
          <path d="M15 3.5v3h3" />
          <path d="M9 11h6M9 14.5h6M9 18h4" />
        </>
      }
    />
  ),
  test: (
    <Icon
      d={
        <>
          <path d="M9.5 3.5v6L5 18a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 18l-4.5-8.5v-6" />
          <path d="M8 3.5h8" />
          <path d="M7 14h10" />
        </>
      }
    />
  ),
};

const NAV_GROUPS = [
  {
    label: 'Workspace',
    sections: [
      {
        key: 'customers',
        label: 'Customers',
        to: '/customers',
        icon: ICONS.customers,
      },
      {
        key: 'orders',
        label: 'Orders',
        to: '/orders',
        icon: ICONS.orders,
      },
      {
        key: 'invoices',
        label: 'Invoices',
        to: '/invoices',
        icon: ICONS.pricing,
      },
      {
        key: 'credit',
        label: 'Credit',
        to: '/credit',
        icon: ICONS.credit,
      },
    ],
  },
  {
    label: 'Catalog',
    hint: 'PIM is the new product model and the place to work. Products is the older integration layer, kept only until migration finishes.',
    sections: [
      {
        key: 'pim',
        label: 'PIM',
        to: '/pim/products',
        match: '/pim',
        icon: ICONS.pim,
        badge: 'New',
        children: [
          { label: 'Products', to: '/pim/products', end: true },
          { label: 'Approvals', to: '/pim/approvals', end: true, count: 'approvals' },
          { label: 'Approval routing', to: '/pim/approvals/rules' },
          { label: 'Name rebuild', to: '/pim/name-rebuild' },
          { label: 'Categories', to: '/pim/categories' },
          { label: 'Auto-Categorisation', to: '/pim/auto-categorisation' },
          { label: 'Image review', to: '/pim/image-review' },
          { label: 'Families & Attributes', to: '/pim/schema' },
          { label: 'Mappings', to: '/pim/mappings' },
          { label: 'Push to Akeneo', to: '/pim/akeneo-push' },
          { label: 'Settings', to: '/pim/settings' },
        ],
      },
      {
        key: 'products',
        label: 'Products',
        to: '/products',
        icon: ICONS.products,
        badge: 'Legacy',
        children: [
          { label: 'Catalog', to: '/products', end: true },
          { label: 'Categorisation', to: '/products/categorisation', end: true },
          { label: 'Uncategorised Queue', to: '/products/categorisation/no-category' },
          { label: 'Category Tree', to: '/products/categorisation/tree' },
          { label: 'Bulk Assign', to: '/products/categorisation/bulk' },
          { label: 'Retire Products', to: '/products/retire', adminOnly: true },
        ],
      },
      {
        key: 'pricing',
        label: 'Pricing',
        to: '/pricing',
        icon: ICONS.pricing,
        children: [
          { label: 'Overview', to: '/pricing', end: true },
          { label: 'Price Lookup', to: '/pricing/lookup' },
        ],
      },
      {
        key: 'inventory',
        label: 'Inventory',
        to: '/inventory',
        icon: ICONS.inventory,
        children: [
          { label: 'Overview', to: '/inventory', end: true },
          { label: 'Stock Levels', to: '/inventory/stock' },
        ],
      },
    ],
  },
  {
    label: 'Operations',
    sections: [
      {
        key: 'manual-syncs',
        label: 'Manual Syncs',
        to: '/manual-syncs',
        icon: ICONS.manualSyncs,
        children: [
          { label: 'Syncs', to: '/manual-syncs', end: true },
          { label: 'Company Cleanup', to: '/manual-syncs/company-cleanup' },
        ],
      },
      {
        key: 'jobs',
        label: 'Jobs',
        to: '/jobs',
        icon: ICONS.jobs,
        children: [
          { label: 'Dashboard', to: '/jobs', end: true },
          { label: 'Job History', to: '/jobs/history' },
          { label: 'Run Sync', to: '/jobs/run' },
          { label: 'Schedules', to: '/jobs/schedules' },
          { label: 'Diff Explorer', to: '/jobs/config/diffs' },
          { label: 'Overrides', to: '/jobs/config/overrides' },
          { label: 'Field Mappings', to: '/jobs/config/mappings' },
          { label: 'Value Lookups', to: '/jobs/config/value-lookups' },
        ],
      },
      {
        key: 'ai-pages',
        label: 'AI Pages',
        to: '/ai-pages',
        icon: ICONS.jobs,
        children: [
          { label: 'Generate Page', to: '/ai-pages', end: true },
        ],
      },
      {
        key: 'configuration',
        label: 'Configuration',
        to: '/configuration',
        match: '/configuration',
        icon: ICONS.configuration,
        children: [
          { label: 'Company settings', to: '/configuration', end: true },
          { label: 'Users & roles', to: '/configuration/users' },
          { label: 'Tags', to: '/configuration/tags', adminOnly: true },
        ],
      },
    ],
  },
  {
    label: 'Test',
    hint: 'Creates real sales orders in the production ERP, restricted to '
      + 'allowlisted test customers. Admin only.',
    sections: [
      {
        key: 'test-checkout',
        label: 'Checkout',
        to: '/test/checkout',
        match: '/test',
        icon: ICONS.test,
        adminOnly: true,
      },
    ],
  },
];

const NAV = NAV_GROUPS.flatMap((g) => g.sections);

/* Breadcrumb page labels for routes that have no NAV child (detail screens). */
const DETAIL_CRUMBS = [
  { pattern: /^\/products\/[^/]+$/, page: 'Product detail' },
  { pattern: /^\/pim\/products\/[^/]+$/, page: 'Product editor' },
  { pattern: /^\/pim\/categories\/[^/]+$/, page: 'Category tree' },
  { pattern: /^\/jobs\/history\/[^/]+$/, page: 'Job detail' },
  { pattern: /^\/jobs\/config\/diffs\/[^/]+\/[^/]+$/, page: 'SKU diff' },
  { pattern: /^\/jobs\/schedules\//, page: 'Schedules' },
  { pattern: /^\/inventory\/stock\/[^/]+\/[^/]+$/, page: 'Stock detail' },
  { pattern: /^\/pricing\/price-lists$/, page: 'Price lists' },
  { pattern: /^\/customers\/[^/]+\/[^/]+$/, page: 'Customer detail' },
  { pattern: /^\/orders\/[^/]+\/[^/]+$/, page: 'Order detail' },
  { pattern: /^\/invoices\/[^/]+\/[^/]+$/, page: 'Invoice detail' },
];

const sectionFor = (pathname) =>
  NAV.find((s) => {
    const base = s.match || s.to;
    return pathname === base || pathname.startsWith(base + '/');
  });

/*
 * Crumb resolution: exact child match wins, then the detail-route map, then a
 * prefix child match (covers nested routes like /jobs/run/epicor), then the
 * section label alone (childless sections render a single crumb).
 */
const crumbsFor = (pathname) => {
  const section = sectionFor(pathname);
  if (!section) return { sectionLabel: null, pageLabel: 'Waterco' };
  const children = section.children || [];

  const exact = children.find((c) => c.to === pathname);
  if (exact) return { sectionLabel: section.label, pageLabel: exact.label };

  const detail = DETAIL_CRUMBS.find((d) => d.pattern.test(pathname));
  if (detail) return { sectionLabel: section.label, pageLabel: detail.page };

  const prefix = children.find((c) => !c.end && pathname.startsWith(c.to + '/'));
  if (prefix) return { sectionLabel: section.label, pageLabel: prefix.label };

  return { sectionLabel: null, pageLabel: section.label };
};

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

const COLLAPSE_KEY = 'appSidebarCollapsed';

const AppShell = ({ children }) => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  // The icon rail is desktop-only; below 1024px the sidebar is a drawer.
  const rail = collapsed && isDesktop;

  // Close the drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Live counts behind the nav badges. Failures are silent: an unreachable or
  // un-migrated workflow API should cost a badge, not the whole shell.
  const [navCounts, setNavCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    const tick = () =>
      getWorkflowSummary()
        .then((s) => {
          if (!cancelled) setNavCounts({ approvals: s?.open_total || 0 });
        })
        .catch(() => {});
    tick();
    const id = setInterval(tick, COUNT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // `adminOnly` children are hidden from non-admins. The page behind the link
  // enforces this itself too — this only keeps destructive tools out of the way
  // of people who cannot use them.
  const childrenFor = (section) =>
    (section.children || []).filter((child) => !child.adminOnly || isAdmin);

  // Sections honour `adminOnly` too, not just children. The Test section is a
  // whole section rather than a child of one, and filtering only children would
  // have shown it to everybody.
  const sectionsFor = (group) =>
    group.sections.filter((section) => !section.adminOnly || isAdmin);

  const countForSection = (section) =>
    childrenFor(section).reduce(
      (total, child) => total + (child.count ? navCounts[child.count] || 0 : 0),
      0,
    );

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });

  const activeSection = sectionFor(location.pathname);
  const { sectionLabel, pageLabel } = crumbsFor(location.pathname);

  return (
    <div className={`app-shell${rail ? ' oc-collapsed' : ''}`}>
      <a className="skip-link" href="#app-main-content">Skip to content</a>

      <aside
        className={`app-sidebar${mobileOpen ? ' open' : ''}`}
        aria-label="Primary navigation"
      >
        <div className="app-brand">
          <NavLink to="/products" className="app-brand-home" title="Waterco Operations Console">
            <span className="app-brand-mark" aria-hidden="true">W</span>
            {!rail && (
              <span className="app-brand-text">
                <span className="app-brand-name">Waterco</span>
                <span className="app-brand-tagline">Operations console</span>
              </span>
            )}
          </NavLink>
          <button
            type="button"
            className="app-collapse-btn"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand navigation' : 'Collapse to icons'}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse to icons'}
            aria-pressed={collapsed}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d={collapsed ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} />
            </svg>
          </button>
        </div>

        <nav className="app-nav" aria-label="Primary">
          {NAV_GROUPS.filter((group) => sectionsFor(group).length > 0).map((group) => (
            <div className="app-nav-group" key={group.label}>
              {!rail && (
                <div className="app-nav-group-label">
                  <span>{group.label}</span>
                  {group.hint && (
                    <span className="app-nav-group-hint" role="img" aria-label={group.hint} title={group.hint}>i</span>
                  )}
                </div>
              )}
              {sectionsFor(group).map((section) => {
                const isActiveSection = activeSection?.key === section.key;
                const pending = countForSection(section);
                return (
                  <div
                    key={section.key}
                    className={`app-nav-section${isActiveSection ? ' expanded' : ''}`}
                  >
                    <NavLink
                      to={section.to}
                      className={() =>
                        'app-nav-link' + (isActiveSection ? ' active' : '')
                      }
                      title={
                        rail
                          ? pending
                            ? `${section.label} — ${pending} awaiting approval`
                            : section.label
                          : undefined
                      }
                    >
                      {section.icon}
                      {/* In rail mode there is no room for a number, so the
                          count degrades to a dot on the icon. */}
                      {rail && pending > 0 && <span className="app-nav-dot" aria-hidden="true" />}
                      {!rail && <span className="app-nav-label">{section.label}</span>}
                      {!rail && pending > 0 && (
                        <span className="app-nav-count">{pending}</span>
                      )}
                      {!rail && section.badge && (
                        <span className="app-nav-badge">{section.badge}</span>
                      )}
                    </NavLink>

                    {!rail && section.children && isActiveSection && (
                      <div className="app-subnav">
                        {childrenFor(section).map((child) => {
                          const childCount = child.count ? navCounts[child.count] || 0 : 0;
                          return (
                            <NavLink
                              key={child.to + child.label}
                              to={child.to}
                              end={child.end}
                              className={({ isActive }) =>
                                'app-subnav-link' + (isActive ? ' active' : '')
                              }
                            >
                              {child.label}
                              {childCount > 0 && (
                                <span className="app-nav-count">{childCount}</span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}

                    {rail && section.children && (
                      <div className="app-flyout" role="menu" aria-label={section.label}>
                        <div className="app-flyout-title">{section.label}</div>
                        {childrenFor(section).map((child) => (
                          <NavLink
                            key={child.to + child.label}
                            to={child.to}
                            end={child.end}
                            className={({ isActive }) =>
                              'app-flyout-link' + (isActive ? ' active' : '')
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {mobileOpen && (
        <div
          className="app-scrim"
          role="presentation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="app-body">
        <header className="app-topbar">
          <button
            type="button"
            className="app-menu-btn"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>

          <nav className="app-crumbs" aria-label="Breadcrumb">
            {sectionLabel && (
              <>
                <span className="app-crumb-section">{sectionLabel}</span>
                <span className="app-crumb-sep" aria-hidden="true">/</span>
              </>
            )}
            {pageLabel && <span className="app-crumb-page">{pageLabel}</span>}
          </nav>

          <div className="app-topbar-actions">
            <AccountControl />
            <ApiKeyControl />
          </div>
        </header>

        <main id="app-main-content" className="app-main sync-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
